import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import { SampleCardData } from './instructor-sample.interface';
import { HE, PROFESSIONAL, SEG, ordinalSuffixOf, reverseCatalogMap } from '../../common/utils/util';
import store, { waitForInstructorData } from '../../store/store';
import { FormBaseComponent } from '../base-component/forms/form-base-component';
import { Product, ShopCart } from '../cart/shop-cart/interfaces/shop-cart.interface';
import { StoreState } from '../../store/store.interface';
import { getCart } from '../cart/shop-cart/store/shop-cart.selectors';
import { getCartItemFormat, getHEsampleExitUrl } from '../../common/utils/cart-util';
import environment from '../../common/environment';
import { getImageSrcUrl, getPdpLink, isHeCustomStoreProduct, isHePrivateStoreProduct } from '../../common/utils/product-util';

@customElement('mhecomm-instructor-sample-product-form')
export class InstructorSampleProductForm extends connect(store)(FormBaseComponent) {
  @property()
  store?: string;

  @state()
  sampleCardData?: SampleCardData | undefined;

  isbn: string | undefined;

  product?: Product | undefined;

  connectedCallback(): void {
    super.connectedCallback();
  }

  async stateChanged(state: StoreState) {
    await waitForInstructorData();
    this.sampleCardData = this.getProductCardData(getCart(state));
  }

  getProductCardData(cart?: ShopCart): SampleCardData | undefined {
    this.product = cart !== undefined ? cart?.products[0] : undefined;
    const sampleCardData: SampleCardData = {
      title: this.product ? this.getProductTitle(this.product) : undefined,
      image: this.product?.productMetadata.imageUrl,
      url: this.product ? this.getCustomStorePdpLink(this.product) : undefined,
      copyRightYear: this.product ? this.getCopyRightYear(this.product) : undefined,
      businessUnit: this.product?.productIdentifier.businessUnit,
      format: this.product?.productIdentifier.businessUnit == HE ? this.getFormatText(this.product) : undefined,
      instructorOnly: false,
      store: '',
      isbn: this.product?.productIdentifier.isbn10,
      isbn13: this.product?.productIdentifier.isbn13,
      mhid: this.product?.productIdentifier.mhid,
      description: '',
      gradeLevels:
        this.product?.productIdentifier.businessUnit == SEG ? this.product?.productMetadata.grades : undefined,
      contributors:
        this.product?.productIdentifier.businessUnit == HE ||
        this.product?.productIdentifier.businessUnit == PROFESSIONAL
          ? this.product.productMetadata.contributors
          : [],
      edition: this.product?.productMetadata.edition,
    };
    return sampleCardData;
  }

  private getFormatText(product: any): string {
    return getCartItemFormat(
      product?.productMetadata?.format,
      product?.productMetadata?.formatCode,
      product?.productMetadata?.virtual
    );
  }

  private getProductTitle(product: Product): string {
    return (
      product.productMetadata.title +
      (product.productMetadata.edition ? ', ' + ordinalSuffixOf(product.productMetadata.edition) + ' Edition' : '')
    );
  }

  private getCopyRightYear(product: Product): string {
    return product.productMetadata.copyrightYear && product.productMetadata.copyrightYear > 0
      ? '' + product.productMetadata.copyrightYear
      : '';
  }

  private getCustomStorePdpLink(product: Product): string {
    const isbn = product?.productIdentifier.isbn13
      ? product.productIdentifier.isbn13
      : product.productIdentifier.isbn10
      ? product.productIdentifier.isbn10
      : product.productIdentifier.mhid;
    let link = '';
    if (this.store) {
      link = new URL(
        `/prek-12/custom/store/${this.store}/product/${product.productIdentifier.mhid}.html`,
        environment().mheducationUrl
      ).href;
    } else if (isbn && (isHeCustomStoreProduct(product) || isHePrivateStoreProduct(product))) {
      link = getPdpLink({
        businessUnit: reverseCatalogMap(product.productIdentifier.businessUnit),
        isbn: isbn,
        productType: product.productIdentifier.productType?.toUpperCase(),
      });
    } else if (isbn) {
      link = getPdpLink({
        businessUnit: reverseCatalogMap(product.productIdentifier.businessUnit),
        isbn: isbn,
      });
    }
    return link;
  }

  public handleRedirect() {
    const redirectUrl: string = getHEsampleExitUrl();
    this.doRedirect(redirectUrl);
  }

  public doRedirect(redirectUrl: string) {
    window.location.href = redirectUrl;
  }

  renderComp() {
    return this.sampleCardData && this.sampleCardData?.isbn
      ? html`
          <div class="mt-4 ms-4"><h2>Requested Sample</h2></div>
          <hr class="hr-primary ms-4 mt-0 col-12" />
          <div class="mb-3 ms-4">
            <div class="row">
              <div class="col-3">
                <img src="${getImageSrcUrl(this.sampleCardData?.isbn!)}|''" class="img-fluid" alt=""/>
              </div>
              <div class="col-9 col-sm-9 col-md-9 col-lg-9 ">
                <h3 class="card-title h5">
                  <span> ${this.sampleCardData.title} </span>
                </h3>
                <div>
                  <span class="fw-bold"> ISBN10:</span> ${this.sampleCardData?.isbn} |
                  <span class="fw-bold">ISBN13: </span> ${this.sampleCardData?.isbn13}
                </div>
                <div>
                  <span class="fw-bold">By:</span> ${this.formatContributors(
                    this.sampleCardData?.contributors ? this.sampleCardData.contributors : []
                  )}
                </div>
                ${this.sampleCardData?.gradeLevels
                  ? html`<div><span class="fw-bold">Grades: </span> ${this.sampleCardData?.gradeLevels}</div>`
                  : ''}
                ${this.sampleCardData?.copyRightYear !== ''
                  ? html`<span>&#169;</span>${this.sampleCardData?.copyRightYear}`
                  : ''}
                <div>
                  ${this.sampleCardData.format
                    ? html`<span class="fw-bold">Format: </span><span>${this.sampleCardData.format}</span><br />`
                    : html``}
                </div>
                <div>
                  <span class="fw-bold">Free</span>
                </div>

                <div>
                  ${this.product?.productMetadata?.virtual == false
                    ? html` <span class="badge bg-light text-dark">This product will be shipped to you.</span>`
                    : ''}
                </div>
              </div>
            </div>
          </div>
        `
      : html``;
  }

  formatContributors(contributors: string[]): string {
    return contributors!.join(' and ');
  }
}